/**
 * Kaching bundle guard — remove orphaned BXGY gift lines.
 *
 * WHY THIS EXISTS (investigated 2026-09-10, brain/kaching-anchor-leak.md):
 * The "Buy 2, Get 1 Free" bar puts TWO line items in the cart — a buyer line
 * (qty 2, property carries `pp`) and a gift line (qty 1, property carries
 * `"bxgy": true`). The gift is free only while its buyer line is present.
 * Delete the buyer line and the gift silently reprices from £0.00 to the full
 * £44.95 anchor and stays in the cart, because every NC variant is priced at
 * the anchor with no compare-at. 64 orders shipped that way between June and
 * September 2026, over-charging customers £1,303.
 *
 * The invariant: a line whose __kaching_bundles has `bxgy: true` is valid only
 * while the cart holds another line for the SAME `deal` carrying `pp`. When
 * that partner goes, so does the gift.
 *
 * Deliberately conservative: it only ever removes a line it can positively
 * parse and positively prove is orphaned. Any parse failure, any network
 * error, anything unexpected — it does nothing and leaves the cart alone.
 */
(function () {
  'use strict';

  var PROP = '__kaching_bundles';
  var busy = false;
  var timer = null;

  function parseTag(line) {
    var props = line && line.properties;
    if (!props) return null;
    var raw = props[PROP];
    if (typeof raw !== 'string' || !raw) return null;
    try {
      var tag = JSON.parse(raw);
      return tag && typeof tag === 'object' ? tag : null;
    } catch (e) {
      return null; // unparseable — treat as not ours
    }
  }

  /** Line keys of gift lines whose buyer line is gone. */
  function findOrphans(cart) {
    if (!cart || !Array.isArray(cart.items)) return [];
    var buyerDeals = Object.create(null);

    cart.items.forEach(function (line) {
      var tag = parseTag(line);
      // A buyer line is one that carries `pp`. It anchors its deal.
      if (tag && tag.pp != null && tag.deal != null) buyerDeals[tag.deal] = true;
    });

    return cart.items.reduce(function (orphans, line) {
      var tag = parseTag(line);
      if (!tag || tag.bxgy !== true) return orphans;
      // No deal id means we cannot prove it is orphaned. Leave it.
      if (tag.deal == null) return orphans;
      if (!buyerDeals[tag.deal]) orphans.push(line.key);
      return orphans;
    }, []);
  }

  function getJSON(url, body) {
    return fetch(url, {
      method: body ? 'POST' : 'GET',
      headers: body ? { 'Content-Type': 'application/json' } : undefined,
      body: body ? JSON.stringify(body) : undefined,
      credentials: 'same-origin'
    }).then(function (r) {
      if (!r.ok) throw new Error(url + ' -> ' + r.status);
      return r.json();
    });
  }

  /*
   * Re-render the cart in place, the way the theme does it, so removing an
   * orphan costs no more than the delete the shopper already clicked. Falling
   * back to location.reload() made a plain delete flash the page twice, which
   * looks broken even though the cart ends up right.
   */
  function cartItemsElement() {
    return document.querySelector('cart-items, cart-drawer-items');
  }

  /*
   * On the cart page we reload. That is a deliberate choice, not laziness.
   *
   * Two smoother approaches were built and both were rejected on test:
   *   - swapping the sections returned by /cart/change.js: that response came
   *     back carrying the cart-bubble section but NOT the items section, so an
   *     "did anything apply?" check reported success and returned early;
   *   - fetching each section fresh from /cart?section_id=...: the swap itself
   *     is correct in isolation (verified: two rows to zero), but a third
   *     script on this store re-renders the cart list from its own snapshot
   *     shortly after, restoring the pre-delete markup.
   *
   * Both leave the page showing rows for a cart that is already empty. A
   * shopper looking at products that are not in their cart is worse than a
   * page flash, so the page reloads and tells the truth. Revisit only if the
   * competing re-render is identified and can be sequenced against.
   *
   * The drawer is a different story: it owns its own markup and redraws
   * cleanly, so there is no reload there.
   */
  function drawerIsOpen(drawer) {
    if (!drawer) return false;
    return drawer.classList.contains('active') ||
           drawer.classList.contains('is-open') ||
           drawer.hasAttribute('open');
  }

  function updateBubble(count) {
    if (count == null) return;
    var bubbles = document.querySelectorAll('.cart-count-bubble span[aria-hidden="true"]');
    for (var i = 0; i < bubbles.length; i++) bubbles[i].textContent = count;
  }

  function refreshUI(cart) {
    updateBubble(cart && cart.item_count);

    var onCartPage = /^\/cart(\/|$|\?)/.test(window.location.pathname);
    if (onCartPage) { window.location.replace('/cart?_kg=' + Date.now()); return; }

    var drawer = document.querySelector('cart-drawer');
    if (drawerIsOpen(drawer) && typeof drawer.onCartUpdate === 'function') {
      try { drawer.onCartUpdate(); } catch (e) { /* nothing else to try */ }
    }
    var items = document.querySelector('cart-drawer-items');
    if (items && typeof items.onCartUpdate === 'function') {
      try { items.onCartUpdate(); } catch (e) { /* nothing else to try */ }
    }
  }

  /*
   * The cart page's own checkout button stays clickable while we are mid-edit.
   * Freeze it so nobody can buy the line we are about to delete.
   */
  var CHECKOUT_SELECTOR = [
    '[name="checkout"]',
    '.cart__checkout-button',
    '#checkout',
    'form[action*="/cart"] [type="submit"]'
  ].join(', ');

  function freezeCheckout(frozen) {
    var nodes = document.querySelectorAll(CHECKOUT_SELECTOR);
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].disabled = frozen;
      nodes[i].style.pointerEvents = frozen ? 'none' : '';
      nodes[i].style.opacity = frozen ? '0.5' : '';
    }
  }

  function removeAll(keys) {
    // Sequential, so we never act on a key invalidated by the previous removal.
    return keys.reduce(function (chain, key) {
      return chain.then(function () {
        return getJSON('/cart/change.js', { id: key, quantity: 0 });
      });
    }, Promise.resolve());
  }

  function reconcile() {
    if (busy) return;
    busy = true;

    getJSON('/cart.js')
      .then(function (cart) {
        var orphans = findOrphans(cart);
        if (!orphans.length) return null;

        console.warn('[kaching-guard] removing ' + orphans.length +
                     ' orphaned gift line(s) with no qualifying bundle line');
        freezeCheckout(true);

        return removeAll(orphans).then(function (cart) {
          return refreshUI(cart);
        });
      })
      .catch(function (e) { console.warn('[kaching-guard] skipped:', e && e.message); })
      .then(function () { freezeCheckout(false); busy = false; });
  }

  function schedule() {
    clearTimeout(timer);
    timer = setTimeout(reconcile, 250);
  }

  function isWatched(url) {
    if (typeof url !== 'string') {
      try { url = String(url); } catch (e) { return false; }
    }
    return /\/cart\/(change|update)(\.js)?(\?|$)/.test(url);
  }

  /*
   * Hooking window.fetch alone proved unreliable on this store: app scripts
   * (Kaching, AfterSell) install their own fetch wrappers, and whichever one
   * captured the native reference first wins — ours was silently bypassed on
   * the cart page's own delete button. So watch the cart from three angles and
   * let the reentrancy guard collapse the duplicates.
   *
   * /cart/add is deliberately NOT watched. An add can never orphan a line, and
   * watching it would race Kaching's own two-request add sequence: the gift and
   * the buyer arrive as separate calls, so a cart observed between them looks
   * orphaned when it is not.
   */

  // 1. The theme's own pub/sub. cart.js publishes this after every mutation it
  //    makes, on the cart page and in the drawer, whatever transport it used.
  (function subscribeWhenReady(tries) {
    var events = window.PUB_SUB_EVENTS;
    if (typeof window.subscribe === 'function' && events && events.cartUpdate) {
      window.subscribe(events.cartUpdate, schedule);
      return;
    }
    if (tries > 0) setTimeout(function () { subscribeWhenReady(tries - 1); }, 200);
  })(25);

  // 2. Cart DOM re-renders, for anything that repaints without publishing.
  function observeCart() {
    if (typeof MutationObserver !== 'function') return;
    var roots = document.querySelectorAll('cart-drawer, cart-items, cart-drawer-items, #main-cart-items, #CartDrawer');
    if (!roots.length) return;
    var observer = new MutationObserver(function () { if (!busy) schedule(); });
    for (var i = 0; i < roots.length; i++) {
      observer.observe(roots[i], { childList: true, subtree: true });
    }
  }

  // 3. Direct cart-API traffic, for anything that bypasses both of the above.
  var nativeFetch = window.fetch;
  if (typeof nativeFetch === 'function') {
    window.fetch = function (input) {
      var url = (input && input.url) ? input.url : input;
      var p = nativeFetch.apply(this, arguments);
      if (isWatched(url)) p.then(function () { schedule(); }, function () {});
      return p;
    };
  }

  var nativeOpen = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    if (isWatched(url)) this.addEventListener('load', schedule);
    return nativeOpen.apply(this, arguments);
  };

  // Catch a cart that was already broken before this script ever ran — including
  // a delete that arrived as a plain link navigation (the no-JS fallback).
  /*
   * THE MAIN PATH. On this store the visible remove control in the cart is the
   * <a href="/cart/change?line=N&quantity=0"> — the sibling <button> is
   * visibility:hidden, and the theme's AJAX re-render does not repaint this
   * page at all. So a delete is a full navigation, and without this handler a
   * shopper gets TWO page loads for one click: the theme's, landing on the
   * orphan at the full anchor price, then ours to clean it up.
   *
   * Intercept the click and take the dependent gift lines out in the SAME
   * request, so a delete costs one navigation — what it cost before this guard
   * existed — and the orphan is never rendered at all.
   *
   * Anything unexpected falls through to the link's own href, so the delete the
   * shopper asked for always happens.
   */
  document.addEventListener('click', function (e) {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;

    var el = e.target;
    if (!el || typeof el.closest !== 'function') return;
    var link = el.closest('a[href*="/cart/change"]');
    if (!link) return;

    var href = link.getAttribute('href') || '';
    var line = /[?&]line=(\d+)/.exec(href);
    var qty = /[?&]quantity=(\d+)/.exec(href);
    if (!line || !qty || qty[1] !== '0') return; // not a "remove this line" link

    var index = parseInt(line[1], 10);
    if (!index) return;

    e.preventDefault();
    busy = true; // hold off the watchers while we rewrite the cart

    getJSON('/cart.js')
      .then(function (cart) {
        var target = cart.items[index - 1];
        if (!target) throw new Error('line ' + index + ' is not in the cart');

        // Work out what the cart looks like after this click, then take out
        // whatever that leaves orphaned — in one update, not a second pass.
        var remaining = cart.items.filter(function (l) { return l.key !== target.key; });
        var updates = {};
        updates[target.key] = 0;

        var orphans = findOrphans({ items: remaining });
        if (orphans.length) {
          console.warn('[kaching-guard] delete also drops ' + orphans.length +
                       ' now-orphaned gift line(s)');
        }
        orphans.forEach(function (key) { updates[key] = 0; });

        return getJSON('/cart/update.js', { updates: updates });
      })
      .then(function () {
        /*
         * Cache-bust the destination. Navigating to a bare /cart straight after
         * a cart mutation renders a STALE page on this store — verified: cart
         * empty, page still drawing a row. A throwaway query param forces a
         * fresh render, and replace() keeps it out of the back button.
         */
        window.location.replace('/cart?_kg=' + Date.now());
      })
      .catch(function (err) {
        console.warn('[kaching-guard] falling back to the plain link:', err && err.message);
        window.location.href = href;
      });
  }, true);

  function start() { observeCart(); reconcile(); }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // A cart restored from bfcache can be stale in either direction.
  window.addEventListener('pageshow', function (e) { if (e.persisted) schedule(); });

  /*
   * 4. KEEP THE BAR THE SHOPPER CHOSE (2026-10-07).
   *
   * Kaching's bar has a built-in "deselect": tapping the bar that is ALREADY
   * selected calls onDealBarDeselect, which drops the selection back to the
   * 1-unit bar. Reproduced on EC and NC, both A/B arms: tap the pre-selected
   * "2 Tubes" once, or "2 Tubes + 1 Free" twice, and ADD TO CART adds ONE
   * tube. Kaching's own config has a flag for this, `ignoreDeselect`, which
   * the app embed ships as false. Set it on every bundle block, once.
   * Changing the config does not reset the current selection (verified).
   */
  function keepSelection() {
    var blocks = document.getElementsByTagName('kaching-bundles-block');
    for (var i = 0; i < blocks.length; i++) {
      try {
        var c = blocks[i].config;
        if (c && typeof c === 'object' && c.ignoreDeselect !== true) {
          blocks[i].config = Object.assign({}, c, { ignoreDeselect: true });
        }
      } catch (e) { /* leave Kaching alone */ }
    }
  }
  if (window.customElements && typeof customElements.whenDefined === 'function') {
    customElements.whenDefined('kaching-bundles-block').then(function () {
      keepSelection();
      // The block's config can arrive a moment after the element upgrades.
      [300, 1000, 3000].forEach(function (ms) { setTimeout(keepSelection, ms); });
      // Blocks rendered later (quick views, re-mounted sections).
      if (typeof MutationObserver !== 'function') return;
      var live = document.getElementsByTagName('kaching-bundles-block'), seen = live.length;
      new MutationObserver(function () {
        if (live.length !== seen) { seen = live.length; keepSelection(); }
      }).observe(document.documentElement, { childList: true, subtree: true });
    });
  }

  /*
   * 5. SHOW THAT CHECKOUT IS LOADING (2026-10-07).
   *
   * SECURE CHECKOUT (drawer and /cart) is a plain form submit to /cart, which
   * redirects to Shopify checkout. On a phone the checkout page takes 1-3 s
   * to start drawing (measured 1.2 s on throttled mobile) and nothing on the
   * button changed meanwhile, so shoppers tapped again and Clarity logged
   * "dead clicks" on "SECURE CHECKOUT • A$255" / "$69.00" — carts that DID
   * reach checkout (both are in abandonedCheckouts). Mark the button busy at
   * once and swallow repeat submits.
   *
   * The button is deliberately NOT disabled: a disabled submitter is dropped
   * from the form data, and `checkout` in the form data is what sends the
   * shopper to checkout instead of back to /cart.
   */
  var CHECKOUT_PENDING_TEXT = 'OPENING SECURE CHECKOUT…';
  function resetCheckoutButtons() {
    var nodes = document.querySelectorAll('[data-checkout-pending]');
    for (var i = 0; i < nodes.length; i++) {
      var b = nodes[i], label = b.querySelector('.button-text');
      if (label && b.__zyLabel != null) label.innerHTML = b.__zyLabel;
      b.removeAttribute('data-checkout-pending');
      b.removeAttribute('aria-busy');
      b.style.opacity = '';
    }
  }
  document.addEventListener('submit', function (e) {
    var btn = e.submitter || document.activeElement;
    if (!btn || btn.name !== 'checkout') return;
    if (btn.hasAttribute('data-checkout-pending')) { e.preventDefault(); return; }
    if (e.defaultPrevented) return; // someone else handles this submit
    btn.setAttribute('data-checkout-pending', '1');
    btn.setAttribute('aria-busy', 'true');
    btn.style.opacity = '0.8';
    var label = btn.querySelector('.button-text');
    if (label) { btn.__zyLabel = label.innerHTML; label.textContent = CHECKOUT_PENDING_TEXT; }
    // Navigation failed or was cancelled: give the button back.
    setTimeout(resetCheckoutButtons, 10000);
  });
  // Back button from checkout restores this page from bfcache, still "pending".
  window.addEventListener('pageshow', function (e) { if (e.persisted) resetCheckoutButtons(); });
})();
